import { addDays } from 'date-fns';

import { ExclusionPeriod } from '~types/appsync.types';

type DateInterval = { start: Date; end: Date };

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function formatDateISO(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function extractTime(date: string): string {
  return date.substring(11, 16);
}

export function parseCalendarDate(dateStr: string): Date {
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function normalizeCalendarDate(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function convertExclusionPeriodsToDateIntervals(
  exclusionPeriods?: ExclusionPeriod[],
): DateInterval[] {
  if (!exclusionPeriods?.length) return [];

  return exclusionPeriods.map((period) => ({
    end: endOfDay(parseCalendarDate(period.endDate)),
    start: startOfDay(parseCalendarDate(period.startDate)),
  }));
}

export function isExcluded(date: Date, excludeIntervals: DateInterval[]): boolean {
  if (!excludeIntervals?.length) return false;

  const timestamp = startOfDay(date).getTime();
  return excludeIntervals.some(
    ({ start, end }) =>
      timestamp >= startOfDay(start).getTime() && timestamp <= startOfDay(end).getTime(),
  );
}

export function isSelectable(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  excludeIntervals: DateInterval[] = [],
): boolean {
  const timestamp = startOfDay(date).getTime();

  if (minDate && timestamp < startOfDay(minDate).getTime()) return false;
  if (maxDate && timestamp > startOfDay(maxDate).getTime()) return false;

  return !isExcluded(date, excludeIntervals);
}

export function getFirstAvailableDate({
  minDate,
  maxDate,
  excludeDateIntervals = [],
}: {
  minDate: Date;
  maxDate?: Date;
  excludeDateIntervals?: DateInterval[];
}): Date {
  let current = startOfDay(minDate);

  while (!isSelectable(current, minDate, maxDate, excludeDateIntervals)) {
    current = addDays(current, 1);
    if (maxDate && current > maxDate) break;
  }

  return current;
}

export function computeMinSelectableDate(validFrom?: Date, now: Date = new Date()): Date {
  if (!validFrom) return now;
  return validFrom > now ? validFrom : now;
}

export function adjustMaxSelectableDate(
  validTo?: Date,
  validityCategoryValue?: number,
): Date | undefined {
  if (!validTo) return undefined;
  if (!validityCategoryValue || validityCategoryValue <= 1) return validTo;

  return addDays(validTo, -(validityCategoryValue - 1));
}

export function isDateInvalidWithValidity(
  date: Date,
  validityCategoryDuration: number,
  productCategoryValidTo?: Date,
): boolean {
  if (!productCategoryValidTo) return false;

  const lastValidDay = addDays(date, validityCategoryDuration - 1);

  return lastValidDay.getTime() > productCategoryValidTo.getTime();
}
